import { useEffect, useRef } from 'react';
import type { editor as MonacoEditor, IDisposable } from 'monaco-editor';

import { isManualPath, MANUAL_READ_ONLY } from '@ai-studio/shared';
import { takeReveal, useCodeReveal } from '../lib/code-reveal.js';
import { languageForPath, monaco } from '../lib/monaco.js';
import { monacoOptions, useEditorPrefs } from '../state/editor-prefs.js';
import { useWorkspace } from '../state/workspace.js';

/**
 * The code editor.
 *
 * Models are keyed by the workspace-relative path and deliberately never
 * destroyed on tab switch, so undo history and scroll position survive moving
 * between tabs.
 */
export function Editor() {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<MonacoEditor.IStandaloneCodeEditor | null>(null);
  const modelsRef = useRef(new Map<string, MonacoEditor.ITextModel>());
  const disposablesRef = useRef<IDisposable[]>([]);
  /** Suppress change events while the store is the source of an update. */
  const applyingRef = useRef(false);

  const projectId = useWorkspace((s) => s.projectId);
  const activePath = useWorkspace((s) => s.tabs.at(-1) ?? null);
  const files = useWorkspace((s) => s.files);
  const setContent = useWorkspace((s) => s.setContent);
  const saveFile = useWorkspace((s) => s.saveFile);

  // Create models for every open file, and drop models for closed ones.
  useEffect(() => {
    for (const file of Object.values(files)) {
      if (modelsRef.current.has(file.path)) continue;
      // A URI gives the language service a stable identity for cross-file
      // features such as rename and go-to-definition.
      const model = monaco.editor.createModel(
        file.content,
        languageForPath(file.path),
        monaco.Uri.parse(`file:///${file.path}`),
      );
      modelsRef.current.set(file.path, model);
    }

    for (const [path, model] of modelsRef.current) {
      if (!files[path]) {
        model.dispose();
        modelsRef.current.delete(path);
      }
    }
  }, [files]);

  // Push external content changes (a save, an undo outside the editor) inward.
  useEffect(() => {
    if (!activePath) return;
    const model = modelsRef.current.get(activePath);
    const file = files[activePath];
    if (!model || !file) return;
    if (model.getValue() !== file.content) {
      applyingRef.current = true;
      model.setValue(file.content);
      applyingRef.current = false;
    }
  }, [activePath, files]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const editor = monaco.editor.create(host, {
      theme: 'ai-studio-dark',
      automaticLayout: true,
      fontFamily: "ui-monospace, 'Cascadia Code', Menlo, Consolas, monospace",
      ...monacoOptions(useEditorPrefs.getState()),
      scrollBeyondLastLine: false,
      smoothScrolling: true,
      renderWhitespace: 'selection',
      bracketPairColorization: { enabled: true },
      guides: { bracketPairs: true, indentation: true },
      padding: { top: 10, bottom: 10 },
      scrollbar: { verticalScrollbarSize: 12, horizontalScrollbarSize: 12 },
    });
    editorRef.current = editor;

    disposablesRef.current.push(
      editor.onDidChangeModelContent(() => {
        if (applyingRef.current) return;
        const model = editor.getModel();
        if (!model) return;
        setContent(model.uri.path.replace(/^\//, ''), model.getValue());
      }),
    );

    // addCommand returns a command id rather than a disposable, so there is
    // nothing to tear down here.
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      const model = editor.getModel();
      if (!model) return;
      const path = model.uri.path.replace(/^\//, '');
      if (isManualPath(path)) return;
      void saveFile(path);
    });

    return () => {
      for (const disposable of disposablesRef.current) disposable.dispose();
      disposablesRef.current = [];
      editor.dispose();
      editorRef.current = null;
    };
  }, [setContent, saveFile]);

  // Settings → Code Editor, applied live.
  const prefs = useEditorPrefs();
  useEffect(() => {
    editorRef.current?.updateOptions(monacoOptions(prefs));
    for (const model of modelsRef.current.values()) model.updateOptions({ tabSize: prefs.tabSize });
  }, [prefs]);

  // Swap the visible model when the active tab changes. The engine manual
  // (.prism/docs/) opens read-only: only Prism writes it.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.setModel(activePath ? (modelsRef.current.get(activePath) ?? null) : null);
    const manual = activePath !== null && isManualPath(activePath);
    editor.updateOptions({ readOnly: manual, readOnlyMessage: { value: MANUAL_READ_ONLY } });
    focusUnlessTyping(editor);
  }, [activePath]);

  // "Go to source" (lib/code-reveal.ts): put the caret on the requested line.
  const revealSeq = useCodeReveal((s) => s.pending?.seq ?? 0);
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !activePath || editor.getModel() !== modelsRef.current.get(activePath)) return;
    const at = takeReveal(activePath);
    if (!at) return;
    const position = { lineNumber: at.line, column: Math.max(1, at.column) };
    editor.setPosition(position);
    editor.revealPositionInCenter(position);
    focusUnlessTyping(editor);
  }, [activePath, files, revealSeq]);

  useEffect(() => () => {
    for (const model of modelsRef.current.values()) model.dispose();
    modelsRef.current.clear();
  }, []);

  if (!projectId) {
    return (
      <div className="editor-empty">
        <p className="muted">Open a project to start editing.</p>
      </div>
    );
  }

  return <div className="editor-host" ref={hostRef} />;
}

/**
 * Focuses the editor unless the user is typing somewhere else (the assistant
 * chat, a search box...): files opened for them, e.g. by Follow assistant,
 * must not swallow their keystrokes.
 */
function focusUnlessTyping(editor: { focus(): void; getDomNode(): HTMLElement | null }): void {
  const active = document.activeElement as HTMLElement | null;
  const typing =
    !!active &&
    (active.tagName === 'TEXTAREA' || active.tagName === 'INPUT' || active.tagName === 'SELECT' || active.isContentEditable);
  if (typing && !editor.getDomNode()?.contains(active)) return;
  editor.focus();
}
